import React from 'react';

import {
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { toast } from 'react-toastify';

import ComponentCard from '../../../components/ComponentCard';
import AppButton from '../../../components/common/AppButton';
import ActionButton from '../../../components/common/ActionButton';
import DataTable from '../../../components/common/DataTable';
import ImagePreviewModal from './category-management/ImagePreviewModal';
import api from '../../../constants/api';
import { showSwal } from '../../../constants/swalConfig';
import BreadCrumbs from '../../../layouts/breadcrumbs/BreadCrumbs';

const emptyBannerForm = {
  redirect: '',
};

const API_BASE_URL = 'https://api.fleetnexis.datadove.tech';

export default function MobileMedia() {
  const [bannerForm, setBannerForm] = React.useState(
    emptyBannerForm,
  );

  const [bannerImage, setBannerImage] = React.useState(null);
  const [imagePreview, setImagePreview] = React.useState('');

  const [errors, setErrors] = React.useState({});

  const [editingBanner, setEditingBanner] = React.useState(null);

  const [previewImage, setPreviewImage] = React.useState('');

  const [banners, setBanners] = React.useState([]);

  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  /*
   * -----------------------------------------
   * Get all banners
   * -----------------------------------------
   */
  const getBanners = React.useCallback(async () => {
    try {
      setLoading(true);

      const response = await api.get('/banner');

      const bannerData = response?.data?.data;

      if (Array.isArray(bannerData)) {
        setBanners(bannerData);
      } else {
        setBanners([]);
      }
    } catch (error) {
      console.error('Failed to fetch banners:', error);

      setBanners([]);

      toast.error(
        error?.response?.data?.message ||
          'Unable to fetch mobile banners.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  /*
   * -----------------------------------------
   * Load banners on page load
   * -----------------------------------------
   */
  React.useEffect(() => {
    getBanners();
  }, [getBanners]);

  /*
   * -----------------------------------------
   * Reset form
   * -----------------------------------------
   */
  const resetBannerForm = () => {
    setBannerForm(emptyBannerForm);
    setBannerImage(null);
    setImagePreview('');
    setErrors({});
    setEditingBanner(null);
  };

  /*
   * -----------------------------------------
   * Handle text field changes
   * -----------------------------------------
   */
  const handleBannerChange = (field, value) => {
    setBannerForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [field]: '',
    }));
  };

  /*
   * -----------------------------------------
   * Handle image selection
   * -----------------------------------------
   */
const handleImageChange = (event) => {
  const file = event.target.files[0] || null;

  setBannerImage(file);

  setErrors((previous) => ({
    ...previous,
    image: '',
  }));

  if (!file) {
    setImagePreview('');
    return;
  }

  const allowedTypes = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/svg+xml',
  ];

  if (!allowedTypes.includes(file.type)) {
    setErrors((previous) => ({
      ...previous,
      image:
        'Please select a PNG, JPG, JPEG, WebP, or SVG image.',
    }));

    setBannerImage(null);
    setImagePreview('');
    return;
  }

  // Maximum file size: 500 KB
  const maxFileSize = 500 * 1024;

  if (file.size > maxFileSize) {
    setErrors((previous) => ({
      ...previous,
      image: 'Banner image size must not exceed 500 KB.',
    }));

    setBannerImage(null);
    setImagePreview('');
    return;
  }

  const previewUrl = URL.createObjectURL(file);

  setImagePreview(previewUrl);
};
 
  const validateBannerForm = () => {
    const newErrors = {};

  
    if (!editingBanner && !bannerImage) {
      newErrors.image = 'Banner image is required.';
    }

  
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

 
  const handleAddBanner = async () => {
    if (!validateBannerForm()) {
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();

      formData.append('banner_image', bannerImage);

      if (bannerForm.redirect.trim()) {
        formData.append(
          'banner_redirect',
          bannerForm.redirect.trim(),
        );
      }

      const response = await api.post(
        '/banner/uploadBanner',
        formData,
      );

      console.log(
        'Add banner response:',
        response?.data,
      );

      toast.success(
        response?.data?.message ||
          'Banner added successfully.',
      );

      resetBannerForm();

      await getBanners();
    } catch (error) {
      console.error('Failed to add banner:', error);

      toast.error(
        error?.response?.data?.message ||
          'Unable to add mobile banner.',
      );
    } finally {
      setSaving(false);
    }
  };


  const hasBannerChanges = () => {
    if (!editingBanner) {
      return true;
    }

    const currentRedirect = bannerForm.redirect.trim();

    const oldRedirect =
      editingBanner.banner_redirect || '';

    return currentRedirect !== oldRedirect;
  };

 
  const handleUpdateBanner = async () => {
  if (!validateBannerForm()) {
    return;
  }

  if (!hasBannerChanges() && !bannerImage) {
    toast.info(
      'No changes made. Please update at least one field.',
    );
    return;
  }

  if (!editingBanner?.banner_id) {
    toast.error('Please select a banner to update.');
    return;
  }

  try {
    setSaving(true);

    const formData = new FormData();

    formData.append(
      'banner_redirect',
      bannerForm.redirect.trim(),
    );

    if (bannerImage) {
      formData.append('banner_image', bannerImage);
    }

    const response = await api.put(
      `/banner/${editingBanner.banner_id}`,
      formData,
    );

    console.log(
      'Update banner response:',
      response?.data,
    );

    toast.success(
      response?.data?.message ||
        'Banner updated successfully.',
    );

    resetBannerForm();

    await getBanners();
  } catch (error) {
    console.error(
      'Failed to update banner:',
      error,
    );

    toast.error(
      error?.response?.data?.message ||
        'Unable to update mobile banner.',
    );
  } finally {
    setSaving(false);
  }
};

  
  const handleSaveBanner = async () => {
    if (editingBanner) {
      await handleUpdateBanner();
      return;
    }

    await handleAddBanner();
  };

  /*
   * -----------------------------------------
   * Edit banner
   * -----------------------------------------
   */
  const handleEditBanner = (banner) => {
    setEditingBanner(banner);

    setBannerForm({
      redirect: banner.banner_redirect || '',
    });

    /*
     * Image replacement is not supported by the
     * documented PUT /banner/{id} API.
     */
    setBannerImage(null);
    setImagePreview('');

    setErrors({});
  };

  /*
   * -----------------------------------------
   * Delete banner
   * DELETE /banner/{id}
   * -----------------------------------------
   */
  const handleDeleteBanner = async (banner) => {
    if (!banner?.banner_id) {
      toast.error('Invalid banner selected.');
      return;
    }

   const result = await showSwal({
  title: 'Delete Banner?',
  text: 'Are you sure you want to delete this banner?',
  icon: 'warning',
  showCancelButton: true,
  confirmButtonText: 'Yes, delete it',
  cancelButtonText: 'Cancel',
});

if (!result.isConfirmed) {
  return;
}

    try {
      setSaving(true);

      const response = await api.delete(
        `/banner/${banner.banner_id}`,
      );

      console.log(
        'Delete banner response:',
        response?.data,
      );

      toast.success(
        response?.data?.message ||
          'Banner deleted successfully.',
      );

      /*
       * If the deleted banner was being edited,
       * clear the form.
       */
      if (
        editingBanner?.banner_id ===
        banner.banner_id
      ) {
        resetBannerForm();
      }

      await getBanners();
    } catch (error) {
      console.error(
        'Failed to delete banner:',
        error,
      );

      toast.error(
        error?.response?.data?.message ||
          'Unable to delete mobile banner.',
      );
    } finally {
      setSaving(false);
    }
  };

  /*
   * -----------------------------------------
   * Convert API image path into URL
   * -----------------------------------------
   */
  const getBannerImageUrl = (imagePath) => {
    if (!imagePath) {
      return '';
    }

    if (
      imagePath.startsWith('http://') ||
      imagePath.startsWith('https://')
    ) {
      return imagePath;
    }

    return `${API_BASE_URL}/${imagePath.replace(
      /^\/+/,
      '',
    )}`;
  };

  /*
   * -----------------------------------------
   * Table columns
   * -----------------------------------------
   */
  const bannerColumns = [
    {
      key: 'index',
      header: 'S.No',
      width: '8%',
      render: (_, index) => index + 1,
    },

    {
      key: 'image',
      header: 'Image',
      width: '20%',
      render: (row) => {
        if (!row.banner_image) {
          return (
            <span className="text-muted">
              No Image
            </span>
          );
        }

        const imageUrl = getBannerImageUrl(
          row.banner_image,
        );

        return (
          <button
            type="button"
            onClick={() =>
              setPreviewImage(imageUrl)
            }
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              cursor: 'pointer',
            }}
            aria-label="Preview banner image"
          >
            <img
              src={imageUrl}
              alt="Banner"
              style={{
                width: '120px',
                height: '60px',
                objectFit: 'cover',
                borderRadius: '6px',
                border: '1px solid #ddd',
                display: 'block',
              }}
            />
          </button>
        );
      },
    },

    {
      key: 'redirect',
      header: 'Banner Redirect',
      width: '42%',
      render: (row) =>
        row.banner_redirect || '-',
    },

    {
      key: 'actions',
      header: 'Actions',
      width: '20%',
      render: (row) => (
        <div className="d-flex align-items-center gap-2">
          <ActionButton
            action="edit"
            onClick={() =>
              handleEditBanner(row)
            }
          />

          <ActionButton
            action="delete"
            onClick={() =>
              handleDeleteBanner(row)
            }
          />
        </div>
      ),
    },
  ];

  return (
    <>
    <BreadCrumbs />
      {/* ---------------------------------
          Add / Edit Banner
      ---------------------------------- */}
      <ComponentCard
        title={
          <span>
            {editingBanner
              ? 'Edit Mobile Banner'
              : 'Add Mobile Banner'}
          </span>
        }
        className="mb-4"
      >
        <Row>
          {/* Banner Image */}
          <Col md="6">
            <FormGroup>
              <Label className="app-text-label">
                Banner Image{' '}
                {!editingBanner && (
                  <small className="text-danger">
                    *
                  </small>
                )}
              </Label>

              <Input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={handleImageChange}
                disabled={saving}
              />

              {errors.image && (
                <div className="field-error">
                  {errors.image}
                </div>
              )}

              {bannerImage && (
                <small className="text-muted d-block mt-1">
                  Selected: {bannerImage.name}
                </small>
              )}

             {editingBanner && (
                <small className="text-muted d-block mt-1">
                  You can select a new image to replace the existing banner.
                </small>
              )}
            </FormGroup>
          </Col>

          {/* Banner Redirect */}
          <Col md="6">
            <FormGroup>
              <Label className="app-text-label">
                Banner Redirect
              </Label>

              <Input
                type="text"
                value={bannerForm.redirect}
                invalid={!!errors.redirect}
                onChange={(event) =>
                  handleBannerChange(
                    'redirect',
                    event.target.value,
                  )
                }
                placeholder="Enter banner redirect (optional)"
                disabled={saving}
              />

              {errors.redirect && (
                <div className="field-error">
                  {errors.redirect}
                </div>
              )}
            </FormGroup>
          </Col>
        </Row>

        {/* Image Preview */}
        {imagePreview && (
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="app-text-label">
                  Image Preview
                </Label>

                <div>
                  <img
                    src={imagePreview}
                    alt="Banner Preview"
                    style={{
                      width: '220px',
                      height: '110px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                    }}
                  />
                </div>
              </FormGroup>
            </Col>
          </Row>
        )}

        {/* Buttons */}
        <div className="d-flex gap-2">
          <AppButton
            variant="primary"
            type="button"
            onClick={handleSaveBanner}
            disabled={saving}
          >
            {saving
              ? 'Saving...'
              : editingBanner
                ? 'Update Banner'
                : 'Save Banner'}
          </AppButton>

          <AppButton
            variant="primary"
            outline
            type="button"
            onClick={resetBannerForm}
            disabled={saving}
          >
            Reset
          </AppButton>
        </div>
      </ComponentCard>

      {/* ---------------------------------
          Existing Banners
      ---------------------------------- */}
      <ComponentCard title="Existing Mobile Banners">
        <DataTable
          columns={bannerColumns}
          data={banners}
          mode="client"
          pageSize={10}
          searchable
          searchPlaceholder="Search banners..."
          emptyMessage={
            loading
              ? 'Loading mobile banners...'
              : 'No mobile banners found.'
          }
          rowKey={(row) => row.banner_id}
        />
      </ComponentCard>

      {/* ---------------------------------
          Image Preview Modal
      ---------------------------------- */}
      <ImagePreviewModal
        isOpen={!!previewImage}
        toggle={() => setPreviewImage('')}
        imageUrl={previewImage}
      />
    </>
  );
}